import {VoiceRecordingOptions} from "../services/chatServiceTypes.ts";
import {EventType, MatrixEvent, Room} from "matrix-js-sdk";
import {OtherUser} from "../hooks/useMatrix.ts";
import {isBotUser} from "../utils/aiBotUtils.ts";
import {formatMessagePreview} from "../utils/messageFormatting.ts";
import {isMessageEdit} from "../utils/messageEdits.ts";
import {stripPlainReplyFallback} from "../utils/privateReplyQuote.ts";
import {MATRIX_CALL_RINGING_EVENT_TYPE} from "../services/matrixCalling/callRinging.ts";
import {MATRIX_CALL_ESCALATE_EVENT_TYPE} from "../services/matrixCalling/callEscalation.ts";
import {RTC_PROTOCOL_V1} from "@phonehq/rtc-core";
import {callPreviewText, callRowKind, type CallRowKind} from "./callRow.ts";
import {
    buildCallTimelineEntries,
    type CallTimelineEvent,
    isCallInviteEvent,
    isCallTimelineEvent,
} from "./callTimeline.ts";
import {ringOutcomes} from "../features/groupCalls/services/ringOutcomes.ts";
import {
    isRtcNotificationEvent,
    rtcNotificationIntent,
    rtcNotificationPreviewText,
} from "./rtcNotification.ts";

export function deduplicateTimelineEvents(events: readonly MatrixEvent[]): MatrixEvent[] {
    const seenEventIds = new Set<string>();
    return events.filter(event => {
        const eventId = event.getId();
        if (!eventId) return true;
        if (seenEventIds.has(eventId)) return false;
        seenEventIds.add(eventId);
        return true;
    });
}

/**
 * Keeps only membership events that change someone's membership. A profile
 * update is join -> join, and two racing joins (the server auto-accepting an
 * invite while the invitee also joins) both claim invite -> join, so each event
 * is compared with the last membership kept for that user, not just its own
 * prev_content.
 */
export function keepMembershipTransitions(events: readonly MatrixEvent[]): MatrixEvent[] {
    const lastMembership = new Map<string, string | undefined>();
    return events.filter(event => {
        if (event.getType() !== EventType.RoomMember) return true;
        const userId = event.getStateKey() ?? '';
        const previous = lastMembership.has(userId)
            ? lastMembership.get(userId)
            : event.getPrevContent()?.membership;
        const membership = event.getContent().membership;
        lastMembership.set(userId, membership);
        return membership !== previous;
    });
}

export type LastEventType = 'text' | 'image' | 'file' | 'voice' | 'event' | 'call' | 'other';

export interface LastEventInfo {
    content: string;
    timestamp: number;
    sender: string;
    senderDisplayName: string;
    type: LastEventType;
    /** Set only for `type: 'call'` — what the room list draws next to it. */
    callKind?: CallRowKind;
}

export interface TranscriptionMessageData {
    audio_event_id?: string;
    text?: string | null;
    error?: string | null;
}

export function getTranscriptionMessageData(content: unknown): TranscriptionMessageData | undefined {
    if (!content || typeof content !== 'object') return undefined;

    const transcriptionContent = content as {
        'org.phonehq.transcript_data'?: TranscriptionMessageData;
        'm.transcript_data'?: TranscriptionMessageData;
    };

    return transcriptionContent['org.phonehq.transcript_data'] || transcriptionContent['m.transcript_data'];
}

export function isTranscriptionMessageContent(content: unknown): boolean {
    if (!content || typeof content !== 'object') return false;

    const transcriptionContent = content as {msgtype?: string};
    if (transcriptionContent.msgtype === 'org.phonehq.transcription_result') return true;

    return transcriptionContent.msgtype === 'm.text' && Boolean(getTranscriptionMessageData(content));
}

export function isSummaryMessageContent(content: unknown): boolean {
    if (!content || typeof content !== 'object') return false;

    const summaryContent = content as {
        msgtype?: string;
        'org.phonehq.summary_data'?: unknown;
        'm.summary_data'?: unknown;
    };

    if (summaryContent.msgtype === 'org.phonehq.summary_result') return true;

    const hasSummaryData = Boolean(summaryContent['org.phonehq.summary_data'] || summaryContent['m.summary_data']);
    return summaryContent.msgtype === 'm.text' && hasSummaryData;
}

export function isRoomMemberJoinEvent(
    event: {
        getContent: () => {membership?: string; [key: string]: unknown};
        getPrevContent: () => {membership?: string; [key: string]: unknown};
    }
): boolean {
    return event.getContent()?.membership === 'join'
        && event.getPrevContent()?.membership !== 'join';
}

const HIDDEN_RTC_EVENT_TYPES: ReadonlySet<string> = new Set([
    RTC_PROTOCOL_V1.declineEventType,
    RTC_PROTOCOL_V1.membershipStateType,
    RTC_PROTOCOL_V1.futureMembershipStateType,
]);

export function isRtcSignalEvent(event: Pick<MatrixEvent, 'getType'>): boolean {
    return HIDDEN_RTC_EVENT_TYPES.has(event.getType());
}

export function isRelevantTimelineEvent(event: MatrixEvent): boolean {
    const eventType = event.getType();

    const sender = event.getSender();
    if (sender && isBotUser(sender)) {
        return false;
    }

    if (eventType === EventType.Reaction || eventType === EventType.RoomRedaction) {
        return false;
    }

    if (eventType === MATRIX_CALL_RINGING_EVENT_TYPE || eventType === MATRIX_CALL_ESCALATE_EVENT_TYPE) {
        return false;
    }

    if (HIDDEN_RTC_EVENT_TYPES.has(eventType)) {
        return false;
    }

    if (isRtcNotificationEvent(event)) {
        return !(typeof event.isRedacted === 'function' && event.isRedacted());
    }

    if (isMessageEdit(event)) {
        return false;
    }

    // One call, one preview. The invite is the call's anchor; answer,
    // candidates and hangup are its plumbing and previewing them left the room
    // list reporting "updated the room" for every leg of a single call.
    if (isCallTimelineEvent(event)) {
        return isCallInviteEvent(event);
    }

    if (eventType === EventType.RoomMessage) {
        const content = event.getContent();
        const msgtype = content?.msgtype;
        return !msgtype?.startsWith('org.phonehq.transcription_')
            && !msgtype?.startsWith('org.phonehq.summary_')
            && !isTranscriptionMessageContent(content)
            && !isSummaryMessageContent(content);
    }
    // Filter out join events
    if (eventType === EventType.RoomMember && event.getContent()?.membership === 'join') {
        return false;
    }
    return true;
}

export interface LastEventOptions {
    isOneToOneRoom?: (room: Room) => boolean;
}

export function formatLastEvent(event: MatrixEvent, room: Room, options: LastEventOptions = {}): LastEventInfo {
    const content = event.getContent();
    const eventType = event.getType();
    const sender = event.getSender() || 'Unknown';
    const timestamp = event.getTs();

    const senderMember = room.getMember(sender);
    const senderDisplayName = senderMember?.rawDisplayName || sender;

    let messageContent = '';
    let messageType: LastEventType = 'other';

    // A redaction empties the event but leaves it in the timeline. In an
    // encrypted room what is left no longer decrypts, so without this the
    // preview reports a decryption failure for a message the user deleted on
    // purpose. The open thread already renders these as "Message deleted".
    if (typeof event.isRedacted === 'function' && event.isRedacted()) {
        return {
            content: 'Message deleted',
            timestamp,
            sender,
            senderDisplayName,
            type: 'text',
        };
    }

    // "Missed call" is the whole preview: the room list prints the time on its
    // own, and repeating it inside the text showed the hour twice.
    if (isCallInviteEvent(event)) {
        const entries = buildCallTimelineEntries(
            (room.getLiveTimeline()?.getEvents() ?? []) as unknown as CallTimelineEvent[],
            room.client?.getUserId?.(),
        );
        const entry = entries.get(event.getId() || '');
        const kind = entry
            ? callRowKind(entry.direction, entry.outcome)
            : callRowKind(sender === room.client?.getUserId?.() ? 'outgoing' : 'incoming', 'ringing');

        return {
            content: callPreviewText(kind),
            timestamp,
            sender,
            senderDisplayName,
            type: 'call',
            callKind: kind,
        };
    }

    if (isRtcNotificationEvent(event)) {
        const oneToOne = options.isOneToOneRoom?.(room) ?? false;
        const eventId = event.getId();
        const missed = !!eventId
            && sender !== room.client?.getUserId?.()
            && ringOutcomes.peek(eventId) === 'missed';
        if (missed) {
            return {
                content: oneToOne ? 'Missed call' : 'Missed group call',
                timestamp,
                sender,
                senderDisplayName,
                type: 'call',
                callKind: 'incoming-missed',
            };
        }
        return {
            content: rtcNotificationPreviewText(rtcNotificationIntent(event), oneToOne),
            timestamp,
            sender,
            senderDisplayName,
            type: 'text',
        };
    }

    if (eventType === EventType.RoomMember) {
        const membership = content.membership;
        switch (membership) {
            case 'join':
                messageContent = `${content.displayname || senderDisplayName} joined`;
                break;
            case 'leave':

                messageContent = event.getStateKey() === sender
                    ? `${senderDisplayName} left`
                    : `${event.sender?.rawDisplayName ?? senderDisplayName} removed user`;
                break;
            case 'invite':
                messageContent = `${senderDisplayName} added user `;
                break;
            case 'ban':
                messageContent = `${senderDisplayName} was banned`;
                break;
            default:
                messageContent = `${senderDisplayName} updated membership`;
        }
        messageType = 'event';
    } else if (eventType === EventType.RoomName) {
        messageContent = content.name
            ? `${senderDisplayName} changed group name to ${content.name}`
            : `${senderDisplayName} removed group name`;
        messageType = 'event';
    } else if (eventType === EventType.RoomTopic) {
        messageContent = content.topic
            ? `${senderDisplayName} changed the topic`
            : `${senderDisplayName} removed the topic`;
        messageType = 'event';
    } else if (eventType === EventType.RoomMessageEncrypted) {
        messageContent = 'Unable to decrypt';
        messageType = 'text';
    } else if (eventType === EventType.RoomEncryption) {
        messageContent = `${senderDisplayName} enabled encryption`;
        messageType = 'event';
    } else if (eventType === EventType.RoomMessage) {
        const isDecryptionError =
            (typeof event.isDecryptionFailure === 'function' && event.isDecryptionFailure()) ||
            content.msgtype === 'm.bad.encrypted';

        if (isDecryptionError) {
            messageContent = 'Unable to decrypt';
            messageType = 'text';
        } else if (content.msgtype === 'm.text') {
            messageContent = formatMessagePreview(stripPlainReplyFallback(content.body || ''));
            messageType = 'text';
        } else if (content.msgtype === 'm.image') {
            messageContent = '📷 Image';
            messageType = 'image';
        } else if (content.msgtype === 'm.file') {
            messageContent = `📎 ${content.filename || 'File'}`;
            messageType = 'file';
        } else if (content.msgtype === 'm.audio') {
            messageContent = '🎵 Voice message';
            messageType = 'voice';
        } else if (content.msgtype === 'm.video') {
            messageContent = '🎥 Video';
            messageType = 'other';
        } else {
            messageContent = formatMessagePreview(stripPlainReplyFallback(content.body || 'Message'));
            messageType = 'other';
        }
    } else {
        messageContent = `${senderDisplayName} updated the room`;
        messageType = 'event';
    }

    return {
        content: messageContent,
        timestamp,
        sender,
        senderDisplayName,
        type: messageType,
    };
}

export const getRoomName = (room: Room | undefined, otherUsers: OtherUser[] | undefined) => {

    if (room?.name) {
        if (room?.name.includes('chat.chte.ch') && otherUsers?.length) {
            return otherUsers.map(user => user.displayName).join(', ')
        }
        return room.name;
    }
}

export async function extractUserIdFromToken(accessToken: string): Promise<string> {
    try {
        const response = await fetch(`${window.__ENV__?.SYNAPSE_BASE_URL}/_matrix/client/v3/account/whoami`, {
            headers: {
                'Authorization': `${accessToken}`,
            },
        });

        if (!response.ok) {
            throw new Error(`Failed to get user info: ${response.status}`);
        }

        const data = await response.json();
        console.log('✅ User ID extracted from token:', data.user_id);
        return data.user_id;
    } catch (error) {
        console.error('Failed to extract user ID from token:', error);

        // Try to decode JWT token as fallback (if your tokens are JWT)
        try {
            const payload = JSON.parse(atob(accessToken.split('.')[1]));
            if (payload.sub) {
                console.log('✅ User ID extracted from JWT payload:', payload.sub);
                return payload.sub;
            }
        } catch (jwtError) {
            console.warn('Could not decode JWT token:', jwtError);
        }

        throw new Error('Could not determine user ID from token');
    }
}

// Enhanced Matrix error handling
export const handleMatrixError = (error: any): string => {
    // Handle encryption-specific errors
    if (error instanceof Error) {
        if (error.message.includes('client does not support encryption')) {
            return 'Encryption not properly initialized. Please refresh the page and try again.';
        }
        if (error.message.includes('configured to use encryption')) {
            return 'This room requires encryption. Initializing encryption support...';
        }
    }

    if (error?.data?.errcode) {
        const {errcode, error: errorMessage} = error.data;
        switch (errcode) {
            case 'M_UNAUTHORIZED':
                return 'Authentication failed. Please log in again.';
            case 'M_FORBIDDEN':
                return 'You do not have permission to perform this action.';
            case 'M_NOT_FOUND':
                return 'The requested resource was not found.';
            case 'M_LIMIT_EXCEEDED':
                return 'Rate limit exceeded. Please try again later.';
            case 'M_USER_IN_USE':
                return 'This username is already taken.';
            case 'M_ROOM_IN_USE':
                return 'This room alias is already in use.';
            case 'M_INVALID_ROOM_STATE':
                return 'The room is in an invalid state for this operation.';
            case 'M_GUEST_ACCESS_FORBIDDEN':
                return 'Guest access is not allowed.';
            case 'M_CAPTCHA_NEEDED':
                return 'Please complete the captcha.';
            case 'M_CAPTCHA_INVALID':
                return 'Invalid captcha response.';
            case 'M_MISSING_TOKEN':
                return 'Missing access token.';
            case 'M_UNKNOWN_TOKEN':
                return 'Invalid or expired access token.';
            case 'M_WEAK_PASSWORD':
                return 'Password is too weak.';
            case 'M_UNRECOGNIZED':
                return 'The server did not understand the request.';
            default:
                return errorMessage || 'An unknown error occurred.';
        }
    }

    // Fallback for other error types
    if (error instanceof Error) {
        return error.message;
    }

    return error?.message || 'An error occurred while communicating with the server.';
};

// Utility functions for token expiration checking
export const isTokenExpired = (error: any): boolean => {
    return (
        error?.status === 401 ||
        error?.data?.errcode === 'M_UNAUTHORIZED' ||
        error?.data?.errcode === 'M_UNKNOWN_TOKEN' ||
        error?.data?.errcode === 'M_MISSING_TOKEN'
    );
};

// Voice recording utilities
export const startVoiceRecording = (options: VoiceRecordingOptions = {}): Promise<MediaRecorder> => {
    return new Promise((resolve, reject) => {
        navigator.mediaDevices.getUserMedia({audio: true})
            .then(stream => {
                const mimeType = options.mimeType || 'audio/webm;codecs=opus';
                const recorder = new MediaRecorder(stream, {
                    mimeType: mimeType,
                    audioBitsPerSecond: options.audioBitsPerSecond || 64000,
                });
                resolve(recorder);
            })
            .catch(reject);
    });
};

export const formatSearchResults = (serverResults: any[]): any[] => {
    return serverResults.map(result => {
        const event = result.result;
        const content = event.content || {};
        const context = result.context || {};

        return {
            eventId: event.event_id,
            roomId: event.room_id,
            sender: event.sender,
            timestamp: event.origin_server_ts,
            body: content.body || '',
            msgtype: content.msgtype || 'm.text',
            formattedBody: content.formatted_body,
            rank: result.rank,
            context: {
                eventsBefore: (context.events_before || []).map((e: any) => ({
                    eventId: e.event_id,
                    sender: e.sender,
                    timestamp: e.origin_server_ts,
                    type: e.type,
                    body: e.content?.body || '',
                    msgtype: e.content?.msgtype
                })),
                eventsAfter: (context.events_after || []).map((e: any) => ({
                    eventId: e.event_id,
                    sender: e.sender,
                    timestamp: e.origin_server_ts,
                    type: e.type,
                    body: e.content?.body || '',
                    msgtype: e.content?.msgtype
                })),
                start: context.start,
                end: context.end
            },
            profile: result.context?.profile_info || {}
        };
    });
}

export function getRoomSortTimestamp(
    room: Room | null,
    lastMessageTimestamp?: number,
    localCreatedAt?: number
): number {
    if (!room) {
        return localCreatedAt || lastMessageTimestamp || 0;
    }

    const bumpStamp = (room as unknown as {getBumpStamp?: () => number | undefined}).getBumpStamp?.();
    if (bumpStamp) return bumpStamp;

    const timelineEvents = room.getLiveTimeline?.()?.getEvents?.() || [];
    const newestEvent = timelineEvents[timelineEvents.length - 1];

    if (newestEvent && isMessageEdit(newestEvent)) {
        for (let i = timelineEvents.length - 1; i >= 0; i--) {
            if (!isMessageEdit(timelineEvents[i])) {
                const ts = timelineEvents[i].getTs();
                if (ts) return ts;
            }
        }
        return lastMessageTimestamp || 0;
    }

    const lastLiveEvent = (room as unknown as {getLastLiveEvent?: () => MatrixEvent | undefined}).getLastLiveEvent?.();
    const lastLiveTs = lastLiveEvent?.getTs?.();
    if (lastLiveTs) return lastLiveTs;

    const lastEventTs = newestEvent ? newestEvent.getTs() : 0;
    if (lastEventTs) return lastEventTs;

    return lastMessageTimestamp || 0;
}

export const categories = [
    'suggested',
    'custom',
    'smileys_people',
    'animals_nature',
    'food_drink',
    'travel_places',
    'activities',
    'objects',
    'symbols',
]
